import { VersionRange } from '../../models/core/version-range.js';
import {
  DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { LocalizedString } from '../../models/serializable/localized-string.js';
import { LegacyPointOfInterestItem } from '../../models/serializable/legacy-point-of-interest-item.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

export class LegacyPointOfInterestItemsComponent extends Component {
  static readonly TYPE_ID: string = 'a5a8d2fd89fe4f05ae4d01dbbb07f5d4';
  static readonly SCHEMA_VERSION: number = 2;
  static readonly DEFAULT_RENDER_ORDER: number = 9;

  constructor(
    public readonly titles: LocalizedString,
    public readonly items: LegacyPointOfInterestItem[],
    public readonly renderOrder: number,
    public readonly isRenderingEnabled: boolean,
  ) {
    super();
  }

  get $typeId() {
    return LegacyPointOfInterestItemsComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, LegacyPointOfInterestItemsComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return true;
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyPointOfInterestItemsComponent {
    return new LegacyPointOfInterestItemsComponent(
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'titles')),
        MapUtils.getList<LegacyPointOfInterestItem>(
            map,
            'items',
            (val) => context.fromData<LegacyPointOfInterestItem>(val as SerializedData),
        ),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'titles': context.toData(this.titles),
      'items': this.items.map((e) => context.toData(e)),
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
    };
  }

  clone(): this {
    return new LegacyPointOfInterestItemsComponent(
        this.titles.clone(),
        this.items.map((e) => e.clone()),
        this.renderOrder,
        this.isRenderingEnabled,
    ) as this;
  }

  isSame(other: LegacyPointOfInterestItemsComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.titles, this.titles) &&
      isSameList(other.items, this.items) &&
      other.renderOrder === this.renderOrder &&
      other.isRenderingEnabled === this.isRenderingEnabled;
  }
}

export class LegacyPointOfInterestItemsComponentBuilder extends DataModelBuilder<LegacyPointOfInterestItemsComponent> {
  build(context: SerializationContext, map: SerializedData): LegacyPointOfInterestItemsComponent {
    return LegacyPointOfInterestItemsComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(LegacyPointOfInterestItemsComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(LegacyPointOfInterestItemsComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', LegacyPointOfInterestItemsComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
